import React, { useEffect } from 'react';
import Form from 'react-bootstrap/Form';
import phone from 'phone';
import { Field, reduxForm, getFormValues, SubmissionError } from 'redux-form';
import { renderCheckbox, renderField } from '../../../lib/helper/form';
import { connect } from 'react-redux';
import { addToast } from '../../../lib/redux/actions/toast';
import { Col, Row } from 'react-bootstrap';
import { ContactRecordTypeCodes } from '../../../apiTypes';

const mapDispatchToProps = {
  addToast,
};

export const email = (value) => (value && !/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(value) ? 'Invalid email address' : undefined);

export const e164 = (value) => {
  if (!value || value.trim() === '') {
    return undefined; // Empty is valid
  }

  // Allow +44 followed by 10-11 digits for UK numbers (mobile and landline)
  // Or any international format with country code
  if (!/^\+[1-9]\d{9,14}$/i.test(value)) {
    return 'Invalid number format (e.g. +447743603123 or +442071234567)';
  }

  return undefined;
};

const mapStateToProps = (state, props) => {
  const currentFormValues = getFormValues(props.form)(state);
  return {
    ...state.contactRecordTypes,
    initialValues: {
      ...props.contact,
      contactRecordType: ContactRecordTypeCodes.Hospital, // Always Hospital type
      isDisabled: props.contact?.isDisabled !== undefined ? props.contact.isDisabled : false, // Default enabled for new contacts
    },
    currentFormValues,
  };
};

export function enforcePhoneNumberPattern(string) {
  // Don't process empty strings
  if (!string || string.trim() === '') {
    return string;
  }

  // First try with GB country code for UK numbers (mobile and landline)
  let validated = phone(string, { country: 'GB' });
  if (validated.isValid) {
    return validated.phoneNumber;
  }

  // Try without country restriction for international numbers
  validated = phone(string, { country: undefined });
  if (validated.isValid) {
    return validated.phoneNumber;
  }

  // If no valid formatting found, return original string
  return string;
}

const HospitalContactRecordForm = ({
  contact,
  handleSubmit,
  error,
  pristine,
  submitting,
  reset,
  toggleFormUnderEdit,
  form,
  contactRecordTypes = [],
  onSubmit,
}) => {
  const handleFormSubmit = (values) => {
    // Validate mobile number format
    //    if (values.mobileNumber && undefined !== e164(values.mobileNumber)) {
    //      throw new SubmissionError({ mobileNumber: e164(values.mobileNumber) });
    //    }

    // Force mobile notifications to false for hospital contacts
    if (values.mobileNumber && String(values.mobileNumber).length > 0) {
      values.notifyByMobile = false; // Always false for hospital contacts
    } else {
      values.notifyByMobile = false;
    }

    // Handle email notifications
    if (!values.email || String(values.email).length < 1) {
      values.notifyByEmail = false;
    }

    // Find Hospital contact record type
    const hospitalContactRecordType = contactRecordTypes.find((o) => o.code === ContactRecordTypeCodes.Hospital);

    const submitVals = {
      ...values,
      contactRecordType: hospitalContactRecordType || { code: ContactRecordTypeCodes.Hospital, name: 'Hospital' },
      // Ensure hospital-specific fields have defaults
      isInitialInvoice: values.isInitialInvoice || false,
      isChaseToContact: values.isChaseToContact || false,
      isDisabled: values.isDisabled !== undefined ? values.isDisabled : false,
      // Force mobile notifications to false
      notifyByMobile: false,
    };

    if (!submitVals?.contactRecordType) {
      throw new SubmissionError({ contactRecordType: 'Contact type must be Hospital' });
    }

    const submitReturn = onSubmit(submitVals);
    toggleFormUnderEdit(form);
    return submitReturn;
  };

  useEffect(() => {
    if (pristine !== undefined && !pristine) {
      toggleFormUnderEdit(form);
    }
  }, [pristine, toggleFormUnderEdit, form]); // Remove form and toggleFormUnderEdit from dependencies to prevent infinite loop

  return (
    <>
      {error && <div className="alert alert-danger">{error}</div>}
      <Form onSubmit={handleSubmit(handleFormSubmit)}>
        <Row>
          <Col sm={12}>
            <Row>
              <Form.Group className={'col-xl-3'}>
                <Field required name="name" label="Contact Name" type="text" component={renderField} />
              </Form.Group>

              <Form.Group className={'col-xl-3'}>
                <Field
                  name="notifyByEmail"
                  id={'notifyByEmail' + contact.id}
                  label="Email Notifications?"
                  component={renderCheckbox}
                  wrapperClassNames={'small float-right mt-1'}
                />
                <Field validate={email} name="email" label="Email Address" type="email" component={renderField} />
              </Form.Group>

              <Form.Group className={'col-xl-3'}>
                <Field
                  name="mobileNumber"
                  label="Phone Number"
                  type="text"
                  component={renderField}
                  //                  validate={e164}
                  //                  normalize={(value) => {
                  //                    if (value && /^0(1|2)\d{9}$/.test(value)) {
                  //                      value = '+44' + value.substring(1);
                  //                    }
                  //
                  //                    return enforcePhoneNumberPattern(value);
                  //                  }}
                  placeholder="e.g. 02071234567 or 07743603123"
                />
              </Form.Group>

              <Form.Group className={'col-xl-3'}>
                <Field
                  name="additionalNumber"
                  label="Additional Number"
                  type="text"
                  component={renderField}
                  //                  validate={e164}
                  //                  normalize={(value) => {
                  //                    if (value && /^0(1|2)\d{9}$/.test(value)) {
                  //                      value = '+44' + value.substring(1);
                  //                    }
                  //
                  //                    return enforcePhoneNumberPattern(value);
                  //                  }}
                />
              </Form.Group>
            </Row>

            {/* Hospital-specific fields */}
            <Row>
              <Col>
                <h5 className="mb-4">Hospital Contact Options</h5>
                <Row>
                  <Form.Group className={'col-xl-3'}>
                    <Field
                      name="isInitialInvoice"
                      id={'isInitialInvoice' + contact.id}
                      label="Is Initial Invoice Contact?"
                      component={renderCheckbox}
                      wrapperClassNames={'mb-2'}
                    />
                    <small className="form-text text-muted">Use this contact for initial invoice communications</small>
                  </Form.Group>

                  <Form.Group className={'col-xl-3'}>
                    <Field
                      name="isChaseToContact"
                      id={'isChaseToContact' + contact.id}
                      label="Is Chase Contact?"
                      component={renderCheckbox}
                      wrapperClassNames={'mb-2'}
                    />
                    <small className="form-text text-muted">Use this contact for chasing communications</small>
                  </Form.Group>

                  <Form.Group className={'col-xl-3'}>
                    <Field name="isDisabled" id={'isDisabled' + contact.id} label="Disable Contact" component={renderCheckbox} wrapperClassNames={'mb-2'} />
                    <small className="form-text text-muted">Disable to exclude from communications</small>
                  </Form.Group>
                </Row>
              </Col>
            </Row>
          </Col>

          <Col>
            <Row>
              <Col>
                {!pristine && (
                  <button type="submit" disabled={submitting} className="btn btn-primary btn-sm w-100 mb-1 mt-4">
                    Save Contact
                  </button>
                )}

                {!pristine && (
                  <button
                    type="button"
                    onClick={() => {
                      toggleFormUnderEdit(form);
                      reset();
                    }}
                    disabled={submitting}
                    className="btn btn-danger btn-sm w-100">
                    Discard Changes
                  </button>
                )}
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(
  reduxForm({
    enableReinitialize: true,
  })(HospitalContactRecordForm),
);
